import {
  groupedByH2ContentAndWrapItWithContainer,
  isStructuredContent,
} from '@repo/contracts/multi-use/structured-content/utils';
import { STRUCTURED_CONTENT_COMPONENTS } from '@repo/structured-content-renderer/config';
import {
  renderStructuredContent,
  setRenderer,
} from '@repo/structured-content-renderer/rendererContext';
import type { RenderDoc, RendererProps } from '@repo/structured-content-renderer/types';
import type { ReactNode } from 'react';
import {
  getAvailableComponents,
  setAvailableComponents,
} from '../contexts/availableComponentsContext';

type Props = {
  doc: RenderDoc;
  rendererProps?: RendererProps;
  hasOverrides?: boolean;
};

function renderDocument({ doc, rendererProps }: Props): ReactNode {
  if (typeof doc === 'string') {
    return <span>{doc}</span>;
  }

  let normalizedDoc: RenderDoc = doc;
  if (isStructuredContent(doc) && rendererProps?.Document?.tableOfContentsEnabled) {
    const rawExcludeHeadings = rendererProps.Document.excludeHeadings;
    const excludeHeadings =
      Array.isArray(rawExcludeHeadings) &&
      rawExcludeHeadings.every(item => typeof item === 'string')
        ? (rawExcludeHeadings as string[])
        : undefined;
    normalizedDoc = groupedByH2ContentAndWrapItWithContainer(doc, excludeHeadings);
  }

  const components = getAvailableComponents();

  return (
    <>
      {[normalizedDoc].flat().map((child, idx) => {
        if (typeof child === 'string') {
          return <span key={`sc-doc-text-${idx}`}>{child}</span>;
        }
        if (!child) {
          return null;
        }

        const Component = components[child.type];

        if (!Component) {
          return null;
        }

        const baseProps =
          child.type === 'AccordionItem'
            ? { ...(child.props ?? {}), structuredContentChildIndex: idx }
            : (child.props ?? {});
        const extraProps = rendererProps?.[child.type];
        const mergedProps = extraProps
          ? { ...baseProps, ...extraProps, rendererProps }
          : { ...baseProps, rendererProps };

        return (
          <Component
            key={`sc-doc-${idx}`}
            type={child.type}
            version={child.version}
            props={mergedProps}
          />
        );
      })}
    </>
  );
}

setRenderer(renderDocument);
setAvailableComponents(STRUCTURED_CONTENT_COMPONENTS);

export function StructuredContentRenderer({ doc, rendererProps, hasOverrides }: Props) {
  return <>{renderStructuredContent(doc, rendererProps, hasOverrides)}</>;
}
